/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo } from 'react';
import { ADMIN_MESSAGE_FORMATS, isHoldAnnouncementFormat } from '../../constants/messageFormats';
import { firstNonEmpty } from '../../components/common/chat/members/memberModalProfileUtils';
import useDelayedVisibility from '../../components/common/chat/shared/useDelayedVisibility';
import { formatDateTimeJST } from '../../utils/datetime';
import useChatDragDrop from '../../components/common/chat/composer/useChatDragDrop';
import { buildToolbarFilterChips } from '../../components/common/chat/search/chatTargetSearchChipUtils';
import { parseReminderTags } from '../../components/common/chat/timeline/systemMessageUtils';
import { buildImageGroupDisplayMessages } from '../../components/common/chat/composer/chatImageGroupUtils';
import { getSummaryEntryTimestamp, groupAdminHoldRequestRows, mapAdminContractSummaryEntry, mapAdminHoldSummaryEntry, sortAndTrimSummaryEntries } from '../../components/common/chat/sidepanel/individualSidePanelSummaryUtils';
import { shouldExcludeAdminSenderForMessage } from '../../components/common/chat/timeline/adminSenderExclusionUtils';
import { openExternalUrl } from '../../utils/externalLink';
import { getSocket } from '../../lib/socket';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import { openAdminChatFollowupTarget } from '../../components/common/chat/followups/chatFollowupNavigation';
import { getAdminArtistDetail } from '../../api/artists';
import { listPeriodContracts, listPeriodHoldRequests, listUpcomingContracts, listUpcomingHoldRequests } from '../../api/castingSummaries';
import { disableAdminNicknamePermission, disableAdminReadReceiptSetting, enableAdminNicknamePermission, enableAdminReadReceiptSetting, getAdminUser, getLatestAdminMessage, listAdminNewMessages, sendAdminFileMessage, sendAdminImageMessages, updateAdminArtistChatNotifyMute } from '../../api/adminChat';
import { API_URL, buildMessageImageUrl, normalizeArtistId, normalizePinnedArtistIds, isSameArtistId, normalizeMessageId, getMessageDedupKey, isAdminPersonalUnreadMessage, isAdminPersonalTargetMessageFormat, isAdminPersonalUnreadPayload, buildRealtimePersonalTextMessage } from '../../components/admin/chats/chatPageHelpers';
import { mergeMessages, patchMessages, removeMessages } from '../../components/common/chat/timeline/timelineMerge';
import { makeLinkPreviewReadyHandler } from '../../components/common/chat/timeline/linkPreviewRealtime';

export default function useAdminChatRuntimeSupport(context) {
  const {
    Error,
    STALE_MS,
    adminArtistChatTone,
    adminMessageCount,
    artistMuteCacheRef,
    artists,
    assigneeFilter,
    bannerTimeoutRef,
    bumpReadOptInVersion,
    canViewArtistProfileBasic,
    canViewContractSummary,
    canViewOfferSummary,
    chatContainerOuterRef,
    chatContainerRef,
    clearArtistPendingRead,
    closeTargetMenu,
    contractAnnouncements,
    ensureArtistInList,
    ensureFlyerUrl,
    ensureReminderSchedule,
    fetchArtistMuteStatus,
    fetchInitialMessages,
    fetchNicknamePermissionStatus,
    fetchOlderMessages,
    fetchReadReceiptSettings,
    flyerCacheRef,
    getArtistLatestTimestamp,
    getGenreLabelFromCache,
    getMessageClass,
    goBackMobile,
    groupFilter,
    groups,
    hasInlineReminderSchedule,
    hasMore,
    hasNewerMessages,
    imageModalHistoryRef,
    isAiTargetSelected,
    isFetching,
    isInitialLoading,
    isMessageSearchModeRef,
    isMobile,
    isPersonalChatAreaOpen,
    isPersonalChatAreaOpenRef,
    isReadOnly,
    isSystemFormat,
    isViewingLatest,
    lastDiffFetchRef,
    loadNewerMessages,
    loadedMessagesArtistId,
    loadingNewerMessages,
    loadingOlderMessages,
    mapMessagesForDisplay,
    markArtistPendingRead,
    markMessagesAsRead,
    messageFilter,
    messages,
    messagesRef,
    mobileChatHistoryRef,
    mobileListPrefRef,
    mobileView,
    nameFilter,
    navigate,
    offerAnnouncements,
    openTargetMenuAt,
    optimisticallyClearAdminPersonalUnread,
    parseInt,
    parseTs,
    partFilter,
    pinnedArtistIds,
    readOptInCacheRef,
    readOptInVersion,
    reconcileTimerRef,
    refreshAdminPersonalUnread,
    refreshAnnouncements,
    refreshMessageReactionDetails,
    refreshPendingMentionTargets,
    reminderCacheRef,
    replyDraft,
    saveLast,
    scrollSidebarToTop,
    scrollToLatest,
    scrollToTop,
    searchDraft,
    selectedArtist,
    selectedArtistId,
    selectedArtistIdRef,
    selectedArtistRef,
    setActiveAnchorMessageId,
    setAdminArtistChatTone,
    setArtistChatMuteLoading,
    setArtistChatMuted,
    setArtistDetail,
    setArtistDetailLoading,
    setArtists,
    setAssigneeFilter,
    setContractAnnouncements,
    setCurrentImageIndex,
    setGroupFilter,
    setHasMore,
    setHasNewerMessages,
    setFollowupRefreshKey,
    setIsAiTargetSelected,
    setIsModalOpen,
    setIsNicknameAllowed,
    setIsReadNotifyEnabled,
    setIsSidePanelLoading,
    setMessageFilter,
    setMessageWindowOffset,
    setMessages,
    setMobileView,
    setNameFilter,
    setOfferAnnouncements,
    setPartFilter,
    setPinnedArtistIds,
    setReadEnabledAdmins,
    setReadToggleLoading,
    setReplyDraft,
    setSearchDraft,
    setSelectedArtist,
    setSendingBusy,
    setSettingsArtist,
    setShowNewMsgBanner,
    setShowSearchModal,
    setShowSettingsModal,
    settingsArtistIdRef,
    settingsArtistRef,
    settingsOpenAllowsReadRef,
    showSettingsModal,
    sidebarPressRef,
    swipeStateRef,
    targetMenu,
    toastError,
    toastSuccess,
    userRef,
  } = context;

  const assigneeQuickToggles = useMemo(
    () => [
      {
        key: 'read-on',
        label: '担当',
        active: assigneeFilter === 'read-on',
        onClick: () => setAssigneeFilter((prev) => (prev === 'read-on' ? '' : 'read-on')),
      },
    ],
    [assigneeFilter]
  );

  const displayedArtists = useMemo(() => {
    const isSearchMode = String(messageFilter || '').trim().length > 0;
    const shouldApplyPinnedOrder = [nameFilter, partFilter, groupFilter, messageFilter, assigneeFilter]
      .every((value) => !String(value || '').trim());
    const baseArtists =
      assigneeFilter !== 'read-on'
        ? artists
        : artists.filter((artist) => {
            const artistKey = normalizeArtistId(artist?.artist_id);
            if (!artistKey) return false;
            return readOptInCacheRef.current.get(artistKey) === true;
          });

    const seenArtistKeys = new Set();
    const deduped = baseArtists.filter((artist) => {
      const artistKey = normalizeArtistId(artist?.artist_id);
      if (!artistKey) return false;
      const searchHitMessageId = normalizeMessageId(artist?.search_hit_message_id);
      const rowKey = isSearchMode && searchHitMessageId ? `${artistKey}:${searchHitMessageId}` : artistKey;
      if (seenArtistKeys.has(rowKey)) return false;
      seenArtistKeys.add(rowKey);
      return true;
    });
    if (!shouldApplyPinnedOrder || pinnedArtistIds.length === 0) return deduped;
    const pinnedRank = new Map(pinnedArtistIds.map((id, index) => [id, index]));
    return deduped
      .map((artist, index) => ({
        artist,
        index,
        rank: pinnedRank.has(normalizeArtistId(artist?.artist_id))
          ? pinnedRank.get(normalizeArtistId(artist?.artist_id))
          : Number.POSITIVE_INFINITY,
      }))
      .sort((left, right) => {
        if (left.rank !== right.rank) return left.rank - right.rank;
        return left.index - right.index;
      })
      .map((entry) => entry.artist);
  }, [artists, assigneeFilter, groupFilter, messageFilter, nameFilter, partFilter, pinnedArtistIds]);

  const isPinnedArtist = useCallback(
    (artist) => pinnedArtistIds.includes(normalizeArtistId(artist?.artist_id)),
    [pinnedArtistIds],
  );

  const togglePinnedArtist = useCallback(
    (artist) => {
      const artistKey = normalizeArtistId(artist?.artist_id).trim();
      if (!artistKey) return;
      setPinnedArtistIds((prev) => {
        const normalized = normalizePinnedArtistIds(prev);
        if (normalized.includes(artistKey)) {
          return normalized.filter((id) => id !== artistKey);
        }
        return [artistKey, ...normalized];
      });
    },
    [],
  );

  const handleTogglePinnedFromTargetMenu = useCallback(() => {
    const artist = targetMenu?.artist;
    if (!artist) return;
    togglePinnedArtist(artist);
    closeTargetMenu();
  }, [closeTargetMenu, targetMenu, togglePinnedArtist]);

  useEffect(() => {
    if (typeof setAdminArtistChatTone !== 'function') return;
    const unreadArtists = artists.filter((artist) => Number(artist?.unread_messages || 0) > 0);
    if (!unreadArtists.length) {
      if (adminMessageCount > 0) return;
      setAdminArtistChatTone(null);
      return;
    }
    const localUnreadTotal = unreadArtists.reduce(
      (sum, artist) => sum + Number(artist?.unread_messages || 0),
      0,
    );
    if (adminMessageCount > 0 && localUnreadTotal < adminMessageCount) return;
    const hasMissingReceipt = unreadArtists.some((artist) => {
      const key = normalizeArtistId(artist?.artist_id);
      return !key || !readOptInCacheRef.current.has(key);
    });
    if (hasMissingReceipt && adminArtistChatTone) return;
    const hasAlert = unreadArtists.some((artist) => {
      const key = normalizeArtistId(artist?.artist_id);
      if (!key) return false;
      if (!readOptInCacheRef.current.has(key)) return false;
      return readOptInCacheRef.current.get(key) === true;
    });
    setAdminArtistChatTone(hasAlert ? 'alert' : 'ok');
  }, [artists, readOptInVersion, adminMessageCount, adminArtistChatTone, setAdminArtistChatTone]);

  const reconcileReadReceipts = useCallback(
    async (targetArtistId) => {
      if (!selectedArtistId) return;
      if (targetArtistId && !isSameArtistId(targetArtistId, selectedArtistId)) return;
      try {
        await fetchInitialMessages(selectedArtistId, { force: true });
      } catch {}
    },
    [fetchInitialMessages, selectedArtistId],
  );

  const scheduleReconcile = useCallback(
    (targetArtistId) => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        reconcileReadReceipts(targetArtistId);
      }, 400);
    },
    [reconcileReadReceipts],
  );

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && selectedArtistId) {
        reconcileReadReceipts(selectedArtistId);
      }
    };
    const onFocus = () => {
      if (selectedArtistId) {
        reconcileReadReceipts(selectedArtistId);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [selectedArtistId, reconcileReadReceipts]);

  useEffect(() => {
    const targets = messages.filter((m) =>
      [
        ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD,
        ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED,
        ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT,
        ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED,
        ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED,
      ].includes(m.message_format),
    );
    targets.forEach((m) => {
      const key = String(m.id || `${m.message_format}-${m.created_at}`);
      if (!flyerCacheRef.current.has(key)) {
        ensureFlyerUrl(m);
      }
    });
    // flyerCacheVersion is intentionally omitted; we just need to react to messages changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, ensureFlyerUrl]);

  useEffect(() => {
    const targets = messages
      .filter((m) => m.message_format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE)
      .map((m) => ({
        id: m.reminder_schedule_id || parseReminderTags(m.content).scheduleId,
        hasInline: hasInlineReminderSchedule(m),
      }))
      .filter(({ id, hasInline }) => id && !hasInline);

    targets.forEach(({ id }) => {
      if (!reminderCacheRef.current.has(id)) {
        ensureReminderSchedule(id);
      }
    });
  }, [messages, ensureReminderSchedule]);

  const syncAdminPersonalUnreadAfterLocalRead = useCallback(
    (clearedUnreadCount = 0) => {
      const cleared = Math.max(0, Number(clearedUnreadCount || 0));
      if (cleared <= 0) return;
      if (typeof optimisticallyClearAdminPersonalUnread === 'function') {
        optimisticallyClearAdminPersonalUnread(cleared);
      }
      if (typeof refreshAdminPersonalUnread === 'function') {
        window.setTimeout(() => {
          refreshAdminPersonalUnread({ verifyIfZero: true });
        }, 0);
      }
    },
    [optimisticallyClearAdminPersonalUnread, refreshAdminPersonalUnread],
  );

  const markAllReadLocal = useCallback((artistOverride = null) => {
    const currentArtist = artistOverride || selectedArtistRef.current;
    const clearedUnreadCount = Math.max(0, Number(currentArtist?.unread_messages || 0));
    if (currentArtist?.artist_id != null) {
      markArtistPendingRead(currentArtist.artist_id);
    }
    setMessages((prev) => {
      if (!prev.some((m) => isAdminPersonalUnreadMessage(m) && !m.is_read)) return prev;
      return prev.map((m) => (isAdminPersonalUnreadMessage(m) ? { ...m, is_read: true } : m));
    });
    setSelectedArtist((prev) =>
      prev && currentArtist && isSameArtistId(prev.artist_id, currentArtist.artist_id)
        ? Number(prev.unread_messages || 0) > 0
          ? { ...prev, unread_messages: 0 }
          : prev
        : prev,
    );
    setArtists((prev) => {
      let changed = false;
      const next = prev.map((a) => {
        if (!currentArtist || !isSameArtistId(a.artist_id, currentArtist.artist_id)) return a;
        if (Number(a.unread_messages || 0) <= 0) return a;
        changed = true;
        return { ...a, unread_messages: 0 };
      });
      return changed ? next : prev;
    });
    syncAdminPersonalUnreadAfterLocalRead(clearedUnreadCount);
  }, [markArtistPendingRead, syncAdminPersonalUnreadAfterLocalRead]);

  const triggerMarkRead = useCallback(async () => {
    if (!selectedArtist) return;
    if (!isPersonalChatAreaOpenRef.current) return;
    try {
      const marked = await markMessagesAsRead(selectedArtist.artist_id);
      if (marked) markAllReadLocal(selectedArtist);
    } catch {}
  }, [markAllReadLocal, markMessagesAsRead, selectedArtist]);

  const handleScroll = useCallback(
    () => {
      const c = chatContainerRef.current;
      if (!c) return;

      const { scrollTop, scrollHeight, clientHeight } = c;
      const needMore = scrollHeight - clientHeight + scrollTop < 50;
      if (needMore && hasMore && !isFetching) {
        fetchOlderMessages();
      }

      const atTop = isViewingLatest();
      if (
        hasNewerMessages &&
        !loadingNewerMessages &&
        atTop
      ) {
        loadNewerMessages();
      }
      // §3.6: 自動既読は「開いている∧タブ前面∧atLatest∧!hasNewer」。
      // ANCHOR で過去窓を開いている間(hasNewerMessages=true)は最新未到達なので既読化しない。
      if (atTop && !hasNewerMessages) {
        const hasUnread = messages.some((m) => isAdminPersonalUnreadMessage(m) && !m.is_read);
        if (hasUnread) {
          triggerMarkRead();
        }
      }
    },
    [
      fetchOlderMessages,
      hasMore,
      hasNewerMessages,
      isFetching,
      isViewingLatest,
      loadNewerMessages,
      loadingNewerMessages,
      messages,
      triggerMarkRead,
    ],
  );

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) chatContainer.addEventListener('scroll', handleScroll);
    return () => {
      if (chatContainer) chatContainer.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const bumpArtistToTop = useCallback((list, aId, patch = {}) => {
    const idx = list.findIndex((a) => isSameArtistId(a.artist_id, aId));
    if (idx === -1) return list;
    const updated = { ...list[idx], ...patch };
    const others = list.slice(0, idx).concat(list.slice(idx + 1));
    return [updated, ...others];
  }, []);

  const handleArtistClick = useCallback(async (artist, { anchorMessageId = '' } = {}) => {
    const normalizedAnchorMessageId = String(anchorMessageId || '').trim();
    const isSameArtist =
      !!selectedArtistRef.current && isSameArtistId(selectedArtistRef.current.artist_id, artist.artist_id);
    if (isSameArtist && !normalizedAnchorMessageId && !isAiTargetSelected) {
      // 既に選択中。モバイルで一覧→チャットへ明示的に遷移
      if (isMobile && mobileView !== 'chat') {
        window.history.pushState({ mobileChat: true }, '', '');
        setMobileView('chat');
        const marked = await markMessagesAsRead(artist.artist_id);
        if (marked) markAllReadLocal(artist);
      }
      return;
    }

    setIsAiTargetSelected(false);
    setMessages([]);
    setHasMore(false);
    setHasNewerMessages(false);
    setMessageWindowOffset(0);
    setActiveAnchorMessageId('');
    setSelectedArtist(artist);

    // モバイルでは「ターゲット選択」ではなく「本文エリアを開く」操作として扱う。
    if (isMobile) {
      window.history.pushState({ mobileChat: true }, '', '');
      setMobileView('chat');
    }

    await fetchInitialMessages(artist.artist_id, {
      anchorMessageId: normalizedAnchorMessageId,
    });
    // §3.6: 検索/リプライの ANCHOR ジャンプで開いた時は最新未到達のため既読化を保留
    // (最下部到達=最新へ落ちた時点で handleScroll 経由で既読化される)。
    if (!normalizedAnchorMessageId) {
      const marked = await markMessagesAsRead(artist.artist_id);
      if (marked) markAllReadLocal(artist);
      setArtists((prevArtists) =>
        prevArtists.map((prevArtist) =>
          isSameArtistId(prevArtist.artist_id, artist.artist_id)
            ? { ...prevArtist, unread_messages: 0 }
            : prevArtist,
        ),
      );
    }

    scrollToTop();
    setHasMore(true);
    saveLast(artist);
  }, [
    fetchInitialMessages,
    isAiTargetSelected,
    isMobile,
    markAllReadLocal,
    markMessagesAsRead,
    mobileView,
    saveLast,
    scrollToTop,
  ]);

  const openAiInbox = useCallback(() => {
    setFollowupRefreshKey((current) => current + 1);
    setIsAiTargetSelected(true);
    if (!isMobile) return;
    mobileListPrefRef.current = false;
    if (!mobileChatHistoryRef.current) {
      try {
        window.history.pushState({ mobileChat: true }, '', '');
        mobileChatHistoryRef.current = true;
      } catch {
        mobileChatHistoryRef.current = false;
      }
    }
    setMobileView('chat');
  }, [isMobile, setFollowupRefreshKey]);

  const handleOpenAiTask = useCallback(
    async (task) => {
      return openAdminChatFollowupTarget({
        task,
        currentChannel: 'personal',
        navigate,
        openPersonal: async ({ artistId, targetName, messageId }) => {
          if (!artistId) return;
          const matchedArtist =
            artists.find((artist) => isSameArtistId(artist?.artist_id, artistId)) || {
              artist_id: artistId,
              name: targetName,
            };
          await handleArtistClick(matchedArtist, { anchorMessageId: messageId });
        },
      });
    },
    [artists, handleArtistClick, navigate],
  );

  const handleGoArtistDetail = useCallback(() => {
    if (!selectedArtistId) return;
    const url = `/admin/workspaces/artist-management/artists/${selectedArtistId}`;
    openExternalUrl(url);
  }, [selectedArtistId]);

  const handleOpenSettingsForArtist = useCallback(async (artist, { selectOnOpen = true } = {}) => {
    if (!artist) return;
    if (selectOnOpen && !isSameArtistId(selectedArtistIdRef.current, artist.artist_id)) {
      await handleArtistClick(artist);
    }
    setSettingsArtist(artist);
    settingsArtistIdRef.current = artist.artist_id;
    settingsArtistRef.current = artist;
    settingsOpenAllowsReadRef.current = isSameArtistId(selectedArtistIdRef.current, artist.artist_id);
    fetchNicknamePermissionStatus(artist.artist_id);
    fetchReadReceiptSettings(artist.artist_id, { withIndicator: true });
    fetchArtistMuteStatus(artist.artist_id, { withIndicator: true });
    setShowSettingsModal(true);
  }, [fetchArtistMuteStatus, fetchNicknamePermissionStatus, fetchReadReceiptSettings, handleArtistClick]);

  const handleOpenSettingsFromTargetMenu = useCallback(() => {
    const artist = targetMenu?.artist;
    if (!artist) return;
    closeTargetMenu();
    handleOpenSettingsForArtist(artist, { selectOnOpen: false });
  }, [closeTargetMenu, handleOpenSettingsForArtist, targetMenu]);

  const handleSettingsButtonClick = () => {
    if (!selectedArtist) return;
    handleOpenSettingsForArtist(selectedArtist, { selectOnOpen: true });
  };

  const clearSidebarLongPress = useCallback(() => {
    if (sidebarPressRef.current.timer) {
      clearTimeout(sidebarPressRef.current.timer);
      sidebarPressRef.current.timer = null;
    }
  }, []);

  const clearSidebarContextMenuBlock = useCallback(() => {
    if (sidebarPressRef.current.contextMenuTimer) {
      clearTimeout(sidebarPressRef.current.contextMenuTimer);
      sidebarPressRef.current.contextMenuTimer = null;
    }
    sidebarPressRef.current.suppressContextMenu = false;
  }, []);

  const blockSidebarContextMenu = useCallback(() => {
    clearSidebarContextMenuBlock();
    sidebarPressRef.current.suppressContextMenu = true;
    sidebarPressRef.current.contextMenuTimer = setTimeout(() => {
      sidebarPressRef.current.suppressContextMenu = false;
      sidebarPressRef.current.contextMenuTimer = null;
    }, 800);
  }, [clearSidebarContextMenuBlock]);

  useEffect(() => {
    const handleContextMenu = (event) => {
      if (!sidebarPressRef.current.suppressContextMenu) return;
      event.preventDefault();
      event.stopPropagation();
      clearSidebarContextMenuBlock();
    };
    window.addEventListener('contextmenu', handleContextMenu, true);
    return () => {
      window.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [clearSidebarContextMenuBlock]);

  const handleSidebarTouchStart = useCallback(
    (artist, event) => {
      if (!isMobile) return;
      clearSidebarLongPress();
      const touch = event?.touches?.[0];
      const clientX = touch?.clientX ?? 16;
      const clientY = touch?.clientY ?? 16;
      sidebarPressRef.current.timer = setTimeout(() => {
        sidebarPressRef.current.timer = null;
        sidebarPressRef.current.suppressClick = true;
        blockSidebarContextMenu();
        openTargetMenuAt(artist, clientX, clientY);
      }, 1000);
    },
    [blockSidebarContextMenu, clearSidebarLongPress, isMobile, openTargetMenuAt],
  );

  const ensureReadNotifyEnabled = useCallback(
    async (artistId) => {
      const cacheKey = String(artistId || '');
      if (!artistId) return false;
      if (readOptInCacheRef.current.get(cacheKey) === true) return false;

      setReadToggleLoading(true);
      try {
        await enableAdminReadReceiptSetting({ artistId });
        readOptInCacheRef.current.set(cacheKey, true);
        bumpReadOptInVersion();
        setIsReadNotifyEnabled(true);
        toastSuccess && toastSuccess('既読通知を自動でオンにしました。');
        await fetchReadReceiptSettings(artistId, { withIndicator: false });
        return true;
      } catch (err) {
        console.error('[readReceipt] auto-enable error:', err);
        toastError && toastError('既読通知の自動オンに失敗しました。');
        return false;
      } finally {
        setReadToggleLoading(false);
      }
    },
    [bumpReadOptInVersion, fetchReadReceiptSettings, toastError, toastSuccess],
  );

  const handleReadNotifyToggle = async (next) => {
    const targetArtist = settingsArtistRef.current || selectedArtist;
    if (!targetArtist) throw new Error('no-artist');
    setReadToggleLoading(true);
    try {
      if (next) {
        await enableAdminReadReceiptSetting({ artistId: targetArtist.artist_id });
      } else {
        await disableAdminReadReceiptSetting({ artistId: targetArtist.artist_id });
      }
      readOptInCacheRef.current.set(String(targetArtist.artist_id), next);
      bumpReadOptInVersion();
      setIsReadNotifyEnabled(next);
      if (
        next &&
        settingsOpenAllowsReadRef.current &&
        isSameArtistId(selectedArtist?.artist_id, targetArtist.artist_id)
      ) {
        const marked = await markMessagesAsRead(targetArtist.artist_id);
        if (marked) markAllReadLocal();
      }
      await fetchReadReceiptSettings(targetArtist.artist_id, { withIndicator: true });
    } catch (err) {
      console.error('[readReceipt] toggle error:', err);
      toastError && toastError('既読通知の更新に失敗しました。');
      throw err; // DsSwitch が表示を巻き戻し
    } finally {
      setReadToggleLoading(false);
    }
  };

  // notifyOn = スイッチON（通知オン）。muted はその反転。
  const handleArtistMuteToggle = async (notifyOn) => {
    const targetArtist = settingsArtistRef.current || selectedArtist;
    if (!targetArtist) throw new Error('no-artist');
    const muted = !notifyOn;
    setArtistChatMuteLoading(true);
    try {
      await updateAdminArtistChatNotifyMute({ artistId: targetArtist.artist_id, muted });
      artistMuteCacheRef.current.set(String(targetArtist.artist_id), muted);
      setArtistChatMuted(muted);
      setSettingsArtist((prev) =>
        prev && isSameArtistId(prev.artist_id, targetArtist.artist_id) ? { ...prev, admin_muted: muted } : prev,
      );
      if (isSameArtistId(selectedArtist?.artist_id, targetArtist.artist_id)) {
        setSelectedArtist((prev) => (prev ? { ...prev, admin_muted: muted } : prev));
      }
      setArtists((prev) =>
        prev.map((artist) =>
          isSameArtistId(artist.artist_id, targetArtist.artist_id)
            ? { ...artist, admin_muted: muted }
            : artist,
        ),
      );
    } catch (err) {
      console.error('[artist-chat mute] toggle error:', err);
      toastError && toastError('このアーティストの通知設定の更新に失敗しました');
      throw err; // DsSwitch が表示を巻き戻し
    } finally {
      setArtistChatMuteLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedArtistId) {
      setIsReadNotifyEnabled(false);
      setReadEnabledAdmins([]);
      setArtistChatMuted(false);
      return;
    }
    if (
      showSettingsModal &&
      settingsArtistIdRef.current &&
      !isSameArtistId(settingsArtistIdRef.current, selectedArtistId)
    ) {
      return;
    }
    let cancelled = false;
    fetchNicknamePermissionStatus(selectedArtistId);
    (async () => {
      await fetchReadReceiptSettings(selectedArtistId);
      if (cancelled) return;
      if (!isPersonalChatAreaOpen) return;
      // §3.6: ANCHOR で過去窓を開いている間(hasNewerMessages=true)は既読化しない。
      // 最新へ到達し hasNewerMessages が false になると本 effect が再走し既読化される。
      if (hasNewerMessages) return;
      const marked = await markMessagesAsRead(selectedArtistId);
      if (marked) markAllReadLocal();
    })();
    fetchArtistMuteStatus(selectedArtistId);
    return () => {
      cancelled = true;
    };
  }, [
    selectedArtistId,
    showSettingsModal,
    fetchReadReceiptSettings,
    isPersonalChatAreaOpen,
    hasNewerMessages,
    markMessagesAsRead,
    markAllReadLocal,
    fetchArtistMuteStatus,
    fetchNicknamePermissionStatus,
  ]);

  const imageMessages = useMemo(
    () => messages.filter((message) => message.message_format === ADMIN_MESSAGE_FORMATS.IMAGE && message.image_file_name),
    [messages],
  );
  const imagePreviewUrls = useMemo(
    () => imageMessages.map((m) => buildMessageImageUrl(m.image_file_name, 'proxy')),
    [imageMessages],
  );
  const imageFullUrls = useMemo(
    () => imageMessages.map((m) => buildMessageImageUrl(m.image_file_name, null)),
    [imageMessages],
  );
  const imageModalItems = useMemo(
    () => imageMessages.map((m) => ({
      ...m,
      messageId: m.id,
      fileName: m.image_file_name,
      src: buildMessageImageUrl(m.image_file_name, 'proxy'),
      fullSrc: buildMessageImageUrl(m.image_file_name, null),
      downloadUrl: buildMessageImageUrl(m.image_file_name, null),
    })),
    [imageMessages],
  );
  const imageIndexByMessageId = useMemo(() => {
    const map = new Map();
    imageMessages.forEach((m, idx) => {
      if (m?.id != null) map.set(m.id, idx);
    });
    return map;
  }, [imageMessages]);
  const displayMessages = useMemo(
    () => buildImageGroupDisplayMessages(messages),
    [messages],
  );

  const handleImageClick = (imageIndex) => {
    if (imageIndex == null || imageIndex < 0 || imageIndex >= imageFullUrls.length) return;
    setCurrentImageIndex(imageIndex);
    setIsModalOpen(true);
    if (!imageModalHistoryRef.current.pushed) {
      try {
        const baseState =
          window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
        window.history.pushState({ ...baseState, chatImageModal: true }, '');
        imageModalHistoryRef.current.pushed = true;
        imageModalHistoryRef.current.closing = false;
      } catch {
        imageModalHistoryRef.current.pushed = false;
        imageModalHistoryRef.current.closing = false;
      }
    }
  };
  const closeModal = (viaPop = false) => {
    setIsModalOpen(false);
    if (!viaPop && imageModalHistoryRef.current.pushed) {
      imageModalHistoryRef.current.closing = true;
      try {
        window.history.back();
      } catch {
        imageModalHistoryRef.current.pushed = false;
        imageModalHistoryRef.current.closing = false;
      }
      return;
    }
    imageModalHistoryRef.current.pushed = false;
    imageModalHistoryRef.current.closing = false;
  };
  const showPrevImage = () =>
    setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? imageFullUrls.length - 1 : prevIndex - 1));
  const showNextImage = () =>
    setCurrentImageIndex((prevIndex) => (prevIndex === imageFullUrls.length - 1 ? 0 : prevIndex + 1));

  /* サイドパネル用サマリー（オファー/本契約） */
  const announcementSummaries = useMemo(() => ({
    offers: offerAnnouncements,
    contracts: contractAnnouncements,
  }), [offerAnnouncements, contractAnnouncements]);

  useEffect(() => {
    let cancelled = false;
    const artistIdNum = selectedArtistId ? String(selectedArtistId) : null;
    if (!artistIdNum) {
      setOfferAnnouncements([]);
      setContractAnnouncements([]);
      setIsSidePanelLoading(false);
      setArtistDetail(null);
      setArtistDetailLoading(false);
      return undefined;
    }

    setIsSidePanelLoading(true);
    setArtistDetailLoading(true);
    setArtistDetail(null);

    const matchesArtist = (item) => {
      const ids = [
        item?.artist_id,
        item?.artistId,
        item?.contract_artist_id,
        item?.hold_casting_artist_id,
        item?.hold_artist_id,
      ]
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v));
      if (ids.includes(artistIdNum)) return true;
      if (Array.isArray(item?.events_all)) {
        return item.events_all.some((ev) => String(ev?.artist_id) === artistIdNum);
      }
      return false;
    };

    const gatherHoldRequests = async () => {
      if (!canViewOfferSummary) return [];
      const bucket = [];
      const requests = [
        () => listUpcomingHoldRequests(),
        () => listPeriodHoldRequests({ start_date: '1970-01-01', end_date: '2100-12-31' }),
      ];
      for (const request of requests) {
        try {
          const data = await request();
          if (data.success === false) throw new Error(data.message || 'fetch error');
          const list = Array.isArray(data.holdRequests) ? data.holdRequests : [];
          bucket.push(...list.filter((r) => matchesArtist(r)));
        } catch (err) {
          console.error('Failed to fetch hold requests for side panel:', err);
        }
      }
      const dedup = new Map();
      groupAdminHoldRequestRows(bucket).forEach((item) => {
        const entry = mapAdminHoldSummaryEntry(item);
        const key = entry.id;
        if (!dedup.has(key) || getSummaryEntryTimestamp(entry) > getSummaryEntryTimestamp(dedup.get(key))) {
          dedup.set(key, entry);
        }
      });
      return sortAndTrimSummaryEntries(Array.from(dedup.values()));
    };

    const gatherContracts = async () => {
      if (!canViewContractSummary) return [];
      const bucket = [];
      const requests = [
        () => listUpcomingContracts(),
        () => listPeriodContracts({ start_date: '1970-01-01', end_date: '2100-12-31' }),
      ];
      for (const request of requests) {
        try {
          const data = await request();
          if (data.success === false) throw new Error(data.message || 'fetch error');
          const list = Array.isArray(data.contracts) ? data.contracts : [];
          bucket.push(...list.filter((r) => matchesArtist(r)));
        } catch (err) {
          console.error('Failed to fetch contracts for side panel:', err);
        }
      }
      const dedup = new Map();
      bucket.forEach((item) => {
        const entry = mapAdminContractSummaryEntry(item);
        const key = entry.id;
        if (!dedup.has(key) || getSummaryEntryTimestamp(entry) > getSummaryEntryTimestamp(dedup.get(key))) {
          dedup.set(key, entry);
        }
      });
      return sortAndTrimSummaryEntries(Array.from(dedup.values()));
    };

    (async () => {
      try {
        const [holds, contracts] = await Promise.all([gatherHoldRequests(), gatherContracts()]);
        if (cancelled) return;
        setOfferAnnouncements(holds);
        setContractAnnouncements(contracts);
      } catch (err) {
        console.error('Failed to update announcement summaries:', err);
        toastError('オファー/本契約情報の取得に失敗しました');
      } finally {
        if (!cancelled) setIsSidePanelLoading(false);
      }
    })();

    (async () => {
      if (!canViewArtistProfileBasic) {
        if (!cancelled) setArtistDetail(null);
        if (!cancelled) setArtistDetailLoading(false);
        return;
      }
      try {
        const data = await getAdminArtistDetail(artistIdNum);
        if (!cancelled) setArtistDetail(data?.artist || data || null);
      } catch (err) {
        console.error('Failed to fetch artist detail for side panel:', err);
        if (!cancelled) setArtistDetail(null);
      } finally {
        if (!cancelled) setArtistDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedArtistId, toastError, canViewContractSummary, canViewOfferSummary, canViewArtistProfileBasic]);


  const handleDownload = (type, id) => {
    const url = `${API_URL}/api/admin/messages/${type === 'image' ? 'images' : 'files'}/${id}/download`;
    openExternalUrl(url);
  };

  const activeSearchFilterCount = useMemo(
    () =>
      [nameFilter.trim(), partFilter, String(groupFilter || '').trim(), messageFilter.trim(), assigneeFilter].filter(Boolean).length,
    [nameFilter, partFilter, groupFilter, messageFilter, assigneeFilter]
  );
  const searchSummaryLabel =
    activeSearchFilterCount > 0
      ? `絞り込み中: ${activeSearchFilterCount}件`
      : '';
  const clearToolbarSearch = useCallback(() => {
    setNameFilter('');
    setPartFilter('');
    setGroupFilter('');
    setMessageFilter('');
    setAssigneeFilter('');
  }, []);
  const searchToolbarChips = useMemo(() => {
    const resolvedGroupName =
      groups.find((group) => String(group?.group_id || '') === String(groupFilter || ''))?.group_name ||
      String(groupFilter || '').trim();
    return buildToolbarFilterChips(
      [
        { key: 'name', label: '名前', value: nameFilter, onRemove: () => setNameFilter('') },
        { key: 'part', label: 'パート', value: partFilter, onRemove: () => setPartFilter('') },
        { key: 'group', label: 'グループ', value: resolvedGroupName, onRemove: () => setGroupFilter('') },
        { key: 'message', label: '本文', value: messageFilter, onRemove: () => setMessageFilter('') },
        {
          key: 'assignee',
          label: '担当',
          value: assigneeFilter === 'read-on' ? '既読ON' : '',
          onRemove: () => setAssigneeFilter(''),
        },
      ],
      { onClearAll: clearToolbarSearch }
    );
  }, [assigneeFilter, clearToolbarSearch, groupFilter, groups, messageFilter, nameFilter, partFilter]);
  const isTargetSearchMode = activeSearchFilterCount > 0;
  const showDelayedInitialLoading = useDelayedVisibility(isInitialLoading, 1000);
  const selectedArtistKey = normalizeArtistId(selectedArtistId);
  const shouldShowChatGuidance = Boolean(
    selectedArtistKey &&
      loadedMessagesArtistId === selectedArtistKey &&
      !isFetching &&
      !isInitialLoading &&
      !loadingOlderMessages &&
      !loadingNewerMessages &&
      !hasMore
  );

  const openTargetSearchModal = useCallback(() => {
    setSearchDraft({
      name: String(nameFilter || ''),
      part: String(partFilter || ''),
      group: String(groupFilter || ''),
      message: String(messageFilter || ''),
    });
    setShowSearchModal(true);
  }, [groupFilter, messageFilter, nameFilter, partFilter]);

  const applyTargetSearchModal = useCallback(() => {
    setNameFilter(String(searchDraft.name || '').trim());
    setPartFilter(String(searchDraft.part || '').trim());
    setGroupFilter(String(searchDraft.group || '').trim());
    setMessageFilter(String(searchDraft.message || '').trim());
    setShowSearchModal(false);
  }, [searchDraft.group, searchDraft.message, searchDraft.name, searchDraft.part]);

  const clearTargetSearchModal = useCallback(() => {
    setSearchDraft({ name: '', part: '', group: '', message: '' });
    setNameFilter('');
    setPartFilter('');
    setGroupFilter('');
    setMessageFilter('');
    setAssigneeFilter('');
    setShowSearchModal(false);
  }, []);

  const updateUnreadMessages = useCallback((aId) => {
    const targetId = normalizeArtistId(aId);
    const activeArtistId = selectedArtistRef.current?.artist_id ?? null;
    if (!targetId) return;
    if (!isSameArtistId(targetId, activeArtistId)) {
      clearArtistPendingRead(targetId);
    }
    setArtists((prevArtists) => {
      const updatedArtists = prevArtists.map((artist) =>
        isSameArtistId(artist.artist_id, targetId)
          ? {
              ...artist,
              unread_messages:
                !isSameArtistId(targetId, activeArtistId)
                  ? (parseInt(artist.unread_messages, 10) || 0) + 1
                  : artist.unread_messages,
            }
          : artist,
      );
      const moved = updatedArtists.find((a) => isSameArtistId(a.artist_id, targetId));
      if (!moved) return updatedArtists;
      const remaining = updatedArtists.filter((a) => !isSameArtistId(a.artist_id, targetId));
      return [moved, ...remaining];
    });
    if (document.visibilityState === 'visible') {
      requestAnimationFrame(() => {
        scrollSidebarToTop();
      });
    }
  }, [clearArtistPendingRead, scrollSidebarToTop]);

  const patchSidebarUnreadMessages = useCallback((aId, delta = 1) => {
    const targetId = normalizeArtistId(aId);
    if (!targetId) return;
    if (delta > 0 && !isSameArtistId(targetId, selectedArtistRef.current?.artist_id)) {
      clearArtistPendingRead(targetId);
    }
    setArtists((prevArtists) =>
      prevArtists.map((artist) => {
        if (!isSameArtistId(artist.artist_id, targetId)) return artist;
        const currentUnread = Number.parseInt(artist.unread_messages, 10) || 0;
        return {
          ...artist,
          unread_messages: Math.max(0, currentUnread + delta),
        };
      })
    );
  }, [clearArtistPendingRead]);

  const refreshSidebarLatest = useCallback(async (aId, { moveToTop = true } = {}) => {
    if (isMessageSearchModeRef.current) return;
    try {
      const { message } = await getLatestAdminMessage(String(aId));

      const toJstDbString = (s) => {
        if (!s) return '';
        const x = String(s).trim();
        if (/(Z|[+-]\d{2}:?\d{2})$/.test(x)) {
          try {
            const label = formatDateTimeJST(x);
            return label.replaceAll('/', '-');
          } catch {}
        }
        return x.replace('T', ' ');
      };

      const created = message ? toJstDbString(message.created_at) : null;
      const patch = message
        ? {
            last_message_time: created,
            last_message: created,
            last_message_type: message.message_type,
            last_message_format: message.message_format,
            last_message_text: message.message_format === 'text' ? message.content : null,
          }
        : {
            last_message_time: null,
            last_message: null,
            last_message_type: null,
            last_message_format: null,
            last_message_text: null,
          };

      setArtists((prev) => {
        if (moveToTop) return bumpArtistToTop(prev, aId, patch);
        const idx = prev.findIndex((a) => isSameArtistId(a.artist_id, aId));
        if (idx === -1) return prev;
        const next = prev.slice();
        next[idx] = { ...next[idx], ...patch };
        next.sort(
          (a, b) =>
            parseTs(getArtistLatestTimestamp(b)) - parseTs(getArtistLatestTimestamp(a)),
        );
        return next;
      });
    } catch {}
  }, [bumpArtistToTop, getArtistLatestTimestamp, parseTs]);

  // §4.1.5/§3.7: サイドバー行の最新プレビューを socket payload からローカル計算して先頭移動。
  // payload だけで賄える(created_at 相当と format が判明する)時は毎受信 GET を避け、
  // 賄えない場合のみ呼び出し側が refreshSidebarLatest(GET フォールバック)へ委ねる。
  // @returns {boolean} ローカル反映できたら true。
  const applySidebarLatestFromPayload = useCallback((aId, payload = {}) => {
    if (isMessageSearchModeRef.current) return false;
    const targetId = normalizeArtistId(aId);
    if (!targetId) return false;
    const format = String(payload?.message_format || payload?.messageFormat || 'text').trim().toLowerCase();
    const messageType =
      String(payload?.message_type || payload?.messageType || '').trim().toLowerCase() ||
      (String(payload?.senderType || payload?.sender_type || '').trim().toLowerCase() === 'admin'
        ? 'admin_message'
        : 'artist_message');
    // text は本文プレビューが payload に無いと賄えない → GET フォールバックへ。
    const text =
      format === 'text'
        ? firstNonEmpty(payload?.content, payload?.text, payload?.preview, payload?.messagePreview)
        : null;
    if (format === 'text' && !text) return false;

    const rawCreated = payload?.created_at || payload?.createdAt || null;
    const toJstDbString = (s) => {
      const base = s ? String(s).trim() : new Date().toISOString();
      if (/(Z|[+-]\d{2}:?\d{2})$/.test(base)) {
        try {
          return formatDateTimeJST(base).replaceAll('/', '-');
        } catch {}
      }
      return base.replace('T', ' ');
    };
    const created = toJstDbString(rawCreated);
    const patch = {
      last_message_time: created,
      last_message: created,
      last_message_type: messageType,
      last_message_format: format,
      last_message_text: format === 'text' ? text : null,
    };
    setArtists((prev) => bumpArtistToTop(prev, targetId, patch));
    return true;
  }, [bumpArtistToTop]);

  const handleShowBanner = useCallback(() => {
    setShowNewMsgBanner(true);
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    bannerTimeoutRef.current = setTimeout(() => setShowNewMsgBanner(false), 3000);
  }, []);

  useEffect(() => {
    const socket = getSocket();
    if (!socket.connected) {
      try { socket.connect(); } catch (_) {}
    }

    // §3.3 RECONNECT: 再接続時に開いている個別チャットを最新へ再同期(silent)。
    // フォーカス復帰/可視化復帰(reconcileReadReceipts, L288-305)と同じ既存経路を再利用する。
    // 検索モード中/チャット非表示中/未選択時は対象外。
    const handleConnect = () => {
      if (isMessageSearchModeRef.current) return;
      if (!isPersonalChatAreaOpenRef.current) return;
      if (!selectedArtistRef.current) return;
      reconcileReadReceipts();
    };

    const handleNewMessage = async (data = {}) => {
      const { artistId: incomingArtistId, adminUserId: senderAdminId } = data;
      const incomingKey = normalizeArtistId(incomingArtistId);
      if (!incomingKey) return;
      refreshPendingMentionTargets();
      const isSearchModeNow = isMessageSearchModeRef.current;
      const currentSelected = selectedArtistRef.current;
      const isIncomingSelected = !!(
        currentSelected && isSameArtistId(incomingKey, currentSelected.artist_id)
      );
      const isIncomingOpenChat = isIncomingSelected && isPersonalChatAreaOpenRef.current;
      let shouldIncrementSidebarUnread = !isIncomingOpenChat;
      const canCountAsUnread = isAdminPersonalUnreadPayload(data);
      const canUpdateTargetLatest = isAdminPersonalTargetMessageFormat(data);
      if (isSearchModeNow && !isIncomingOpenChat) {
        if (canCountAsUnread) {
          patchSidebarUnreadMessages(incomingKey, 1);
        }
        return;
      }

      let existedBefore = true;
      if (!isIncomingOpenChat && canUpdateTargetLatest) {
        const ensured = await ensureArtistInList(incomingKey);
        existedBefore = !!ensured?.existedBefore;
      }

      if (!currentSelected || !isIncomingOpenChat) {
        if (existedBefore && canCountAsUnread) updateUnreadMessages(incomingKey);
        if (!isSearchModeNow && canUpdateTargetLatest) {
          // §4.1.5: payload で賄えれば GET を省略、賄えない時のみフォールバック。
          if (!applySidebarLatestFromPayload(incomingKey, data)) {
            await refreshSidebarLatest(incomingKey);
          }
        }
        return;
      }

      const currentUser = userRef.current;
      const realtimeMessage = buildRealtimePersonalTextMessage(data);
      if (realtimeMessage) {
        const atTopBefore = isViewingLatest();
        const [displayMessage] = await mapMessagesForDisplay([realtimeMessage]);
        if (displayMessage) {
          // 経路①: enriched payload の直接 merge(§3.3 SOCKET_MSG)。
          setMessages((prev) => mergeMessages(prev, [displayMessage], { getKey: getMessageDedupKey }));
          lastDiffFetchRef.current = Date.now();
          const hasUnreadAddition = isAdminPersonalUnreadMessage(displayMessage);
          const isSelfAdminMessage =
            senderAdminId &&
            currentUser &&
            String(senderAdminId) === String(currentUser.userId);

          if (atTopBefore) {
            requestAnimationFrame(() => {
              scrollToLatest();
            });
          } else if (hasUnreadAddition && !isSelfAdminMessage) {
            handleShowBanner();
          }

          if (
            hasUnreadAddition &&
            !isSelfAdminMessage &&
            isPersonalChatAreaOpenRef.current &&
            atTopBefore
          ) {
            const marked = await markMessagesAsRead(incomingKey);
            if (marked) {
              markAllReadLocal();
              setMessages((prev) =>
                patchMessages(
                  prev,
                  prev.filter(isAdminPersonalUnreadMessage).map((m) => m.id),
                  { is_read: true },
                ),
              );
            }
          }
        }
        if (!isSearchModeNow && isAdminPersonalTargetMessageFormat(realtimeMessage)) {
          if (!applySidebarLatestFromPayload(incomingKey, data)) {
            await refreshSidebarLatest(incomingKey);
          }
        }
        return;
      }

      if (senderAdminId && currentUser && String(senderAdminId) === String(currentUser.userId)) {
        if (!isSearchModeNow && isAdminPersonalTargetMessageFormat(data)) {
          if (!applySidebarLatestFromPayload(incomingKey, data)) {
            await refreshSidebarLatest(incomingKey);
          }
        }
        return;
      }

      const stale = Date.now() - lastDiffFetchRef.current > STALE_MS;
      let hasTargetAdditions = false;
      try {
        const currentMessages = messagesRef.current;
        const newest = currentMessages[0]?.created_at;
        if (!newest || stale) {
          if (!isSearchModeNow && canUpdateTargetLatest) {
            await refreshSidebarLatest(incomingKey);
          }
          lastDiffFetchRef.current = Date.now();
          return;
        }
        const { messages: fetched = [] } = await listAdminNewMessages(incomingKey, String(newest));
        lastDiffFetchRef.current = Date.now();

        const existIds = new Set(currentMessages.map((m) => m.id));
        const additionsAsc = await Promise.all(
          fetched
            .filter((m) => !existIds.has(m.id))
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .map(async (m) => {
              let admin_name = '';
              if (
                m.message_type === 'admin_message' &&
                m.admin_user_id &&
                !shouldExcludeAdminSenderForMessage(m)
              ) {
                try {
                  const ad = await getAdminUser(Number(m.admin_user_id));
                  admin_name = m.is_nickname_allowed
                    ? ad?.nickname || ad?.full_name || ad?.username || ''
                    : ad?.full_name || ad?.username || ad?.nickname || '';
                } catch {
                  admin_name = '';
                }
              }
              return {
                ...m,
                admin_name,
                isSystem: isSystemFormat(m.message_format),
                className: getMessageClass(m),
                genreLabel:
                  isHoldAnnouncementFormat(m.message_format)
                    ? getGenreLabelFromCache(m.event_genre)
                    : undefined,
              };
            }),
        );

        if (!additionsAsc.length) {
          if (!isSearchModeNow && canUpdateTargetLatest) {
            await refreshSidebarLatest(incomingKey);
          }
          return;
        }

        const atTopBefore = isViewingLatest();
        const hasUnreadAdditions = additionsAsc.some(isAdminPersonalUnreadMessage);
        hasTargetAdditions = additionsAsc.some(isAdminPersonalTargetMessageFormat);

        // 経路②: スケルトン payload → new-messages 差分フェッチ結果を merge(§3.3 SOCKET_MSG)。
        setMessages((prev) => mergeMessages(prev, additionsAsc, { getKey: getMessageDedupKey }));

        if (isPersonalChatAreaOpenRef.current && atTopBefore && hasUnreadAdditions) {
          requestAnimationFrame(() => {
            scrollToLatest();
          });
          const marked = await markMessagesAsRead(incomingKey);
          if (marked) {
            markAllReadLocal();
            shouldIncrementSidebarUnread = false;
          }
        } else if (atTopBefore) {
          requestAnimationFrame(() => {
            scrollToLatest();
          });
        } else {
          handleShowBanner();
          shouldIncrementSidebarUnread = hasUnreadAdditions;
        }
      } catch {}

      if (!isSearchModeNow) {
        if (shouldIncrementSidebarUnread) {
          updateUnreadMessages(incomingKey);
        }
        if (canUpdateTargetLatest || hasTargetAdditions) {
          await refreshSidebarLatest(incomingKey);
        }
      }
    };

    const handleMessagesRead = ({ artistId: aId, readMessageIds } = {}) => {
      const currentSelected = selectedArtistRef.current;
      if (
        currentSelected &&
        isSameArtistId(aId, currentSelected.artist_id) &&
        Array.isArray(readMessageIds)
      ) {
        setMessages((prev) => patchMessages(prev, readMessageIds, { is_read: true }));
      }
      if (currentSelected && isSameArtistId(currentSelected.artist_id, aId)) {
        scheduleReconcile(aId);
      }
    };

    const handleMessageDeleted = ({ artistId: aId, messageId, messageIds } = {}) => {
      const deletedIds = Array.isArray(messageIds) && messageIds.length
        ? messageIds.map((id) => String(id))
        : (messageId ? [String(messageId)] : []);
      if (!aId || deletedIds.length === 0) return;
      const currentSelected = selectedArtistRef.current;
      if (currentSelected && isSameArtistId(aId, currentSelected.artist_id)) {
        setMessages((prev) => removeMessages(prev, deletedIds));
      }
      if (!isMessageSearchModeRef.current) {
        refreshSidebarLatest(aId, { moveToTop: false });
      }
    };

    const handleAnnouncementsUpdated = ({ artistId: aId } = {}) => {
      const currentSelected = selectedArtistRef.current;
      if (currentSelected && isSameArtistId(aId, currentSelected.artist_id)) {
        refreshAnnouncements({ silent: true }).catch(() => {});
      }
    };

    const handleReactionUpdated = ({ artistId: aId, messageId } = {}) => {
      const currentSelected = selectedArtistRef.current;
      if (!currentSelected || !isSameArtistId(aId, currentSelected.artist_id) || !messageId) return;
      refreshMessageReactionDetails(messageId).catch(() => {});
    };

    const handleLinkPreviewReady = makeLinkPreviewReadyHandler(setMessages);

    socket.on('connect', handleConnect);
    socket.on(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
    socket.on(SOCKET_EVENTS.MESSAGES_READ, handleMessagesRead);
    socket.on(SOCKET_EVENTS.MESSAGE_DELETED, handleMessageDeleted);
    socket.on(SOCKET_EVENTS.CHAT_ANNOUNCEMENTS_UPDATED, handleAnnouncementsUpdated);
    socket.on(SOCKET_EVENTS.CHAT_MESSAGE_REACTION_UPDATED, handleReactionUpdated);
    socket.on(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);

    return () => {
      socket.off('connect', handleConnect);
      socket.off(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
      socket.off(SOCKET_EVENTS.MESSAGES_READ, handleMessagesRead);
      socket.off(SOCKET_EVENTS.MESSAGE_DELETED, handleMessageDeleted);
      socket.off(SOCKET_EVENTS.CHAT_ANNOUNCEMENTS_UPDATED, handleAnnouncementsUpdated);
      socket.off(SOCKET_EVENTS.CHAT_MESSAGE_REACTION_UPDATED, handleReactionUpdated);
      socket.off(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ensureArtistInList,
    getGenreLabelFromCache,
    handleShowBanner,
    isViewingLatest,
    markAllReadLocal,
    markMessagesAsRead,
    mapMessagesForDisplay,
    refreshSidebarLatest,
    applySidebarLatestFromPayload,
    refreshAnnouncements,
    refreshPendingMentionTargets,
    refreshMessageReactionDetails,
    reconcileReadReceipts,
    scheduleReconcile,
    scrollToLatest,
    updateUnreadMessages,
    patchSidebarUnreadMessages,
  ]);

  const handleBannerClick = async () => {
    setShowNewMsgBanner(false);
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    try {
      if (selectedArtist?.artist_id) {
        setActiveAnchorMessageId('');
        setHasNewerMessages(false);
        await fetchInitialMessages(selectedArtist.artist_id, { force: true, anchorMessageId: '' });
      }
      scrollToLatest();
      triggerMarkRead();
    } catch (error) {
      console.error('[ChatPage] failed to jump latest from banner:', error);
    }
  };

  // ★ ここをサーバー反映に修正（POST/DELETE）＋完了後リロード
  // 楽観反映/ロールバックは DsSwitch が担当。ここは保存と確定状態のみ。
  const handleNicknamePermissionChange = async (next) => {
    const targetArtist = settingsArtistRef.current || selectedArtist;
    if (!targetArtist) throw new Error('no-artist');
    const checked = !!next;
    try {
      if (checked) {
        await enableAdminNicknamePermission({ artistId: targetArtist.artist_id });
        toastSuccess && toastSuccess('ニックネームを許可しました。');
      } else {
        await disableAdminNicknamePermission({ artistId: targetArtist.artist_id });
        toastSuccess && toastSuccess('ニックネーム許可を解除しました。');
      }
      setIsNicknameAllowed(checked);
      if (isSameArtistId(selectedArtist?.artist_id, targetArtist.artist_id)) {
        await fetchInitialMessages(targetArtist.artist_id, { force: true });
      }
    } catch (err) {
      toastError('更新に失敗しました。');
      throw err; // DsSwitch が表示を巻き戻し
    }
  };

  // 入力欄の送信後フック
  const handleComposerSent = useCallback(async () => {
    if (isReadOnly) {
      toastError && toastError('現在メンテナンス中のため送信できません。');
      return;
    }
    if (!selectedArtist) return;
    const autoEnabled = await ensureReadNotifyEnabled(selectedArtist.artist_id);
    await fetchInitialMessages(selectedArtist.artist_id);
    await refreshSidebarLatest(selectedArtist.artist_id);
    refreshPendingMentionTargets();
    const marked = await markMessagesAsRead(selectedArtist.artist_id, { force: autoEnabled });
    if (marked) markAllReadLocal();
    setReplyDraft(null);
  }, [
    ensureReadNotifyEnabled,
    fetchInitialMessages,
    isReadOnly,
    markAllReadLocal,
    markMessagesAsRead,
    refreshPendingMentionTargets,
    refreshSidebarLatest,
    selectedArtist,
    setReplyDraft,
    toastError,
  ]);

  // ====== DnD：実際の送信API ======
  const sendDroppedImages = useCallback(async (aId, files) => {
    const fd = new FormData();
    fd.append('artistId', String(aId));
    files.forEach((f) => fd.append('images', f, f.name));
    if (replyDraft?.messageId) fd.append('replyToMessageId', String(replyDraft.messageId));
    await sendAdminImageMessages(fd);
    return {};
  }, [replyDraft]);
  const sendDroppedFile = useCallback(async (aId, file) => {
    const fd = new FormData();
    fd.append('artistId', String(aId));
    fd.append('file', file, file.name);
    if (replyDraft?.messageId) fd.append('replyToMessageId', String(replyDraft.messageId));
    await sendAdminFileMessage(fd);
    return {};
  }, [replyDraft]);

  // 画像/ファイルの選択送信でも BusyOverlay を出すためのフック
  // kind: 'image' | 'file' | 'text' | 'unknown'
  const handleUploading = useCallback((payload) => {
    if (typeof payload === 'boolean') {
      setSendingBusy(payload);
      return;
    }
    const { active = false, kind = 'unknown', count } = payload || {};
    if (kind === 'text') {
      setSendingBusy(false);
      return;
    }
    setSendingBusy(!!active);
    if (active) return;
    if ((kind === 'image' || kind === 'images') && typeof count === 'number') {
      toastSuccess && toastSuccess(`${count}件の画像を送信しました。`);
    } else if (kind === 'file' && typeof count === 'number') {
      toastSuccess && toastSuccess(`${count}件のファイルを送信しました。`);
    }
  }, [toastSuccess]);

  /* =========================
   *  D&D: 監視＝メッセージ領域（スクロールする要素）
   *        枠表示＝外枠（スクロールしない要素）
   * ========================= */
  const handleDropFiles = useCallback(
    async (files, { rejectedCount }) => {
      if (isReadOnly) {
        toastError && toastError('現在メンテナンス中のため送信できません。');
        return;
      }
      if (!selectedArtist) return;

      if (rejectedCount > 0) {
        toastError && toastError('サイズ上限(100MB)を超えるファイルを除外しました。');
      }

      let images = files.filter((f) => /^image\//i.test(f.type));
      const others = files.filter((f) => !/^image\//i.test(f.type));

      if (images.length > 5) {
        images = images.slice(0, 5);
        toastError && toastError('画像は最大5枚まで送信できます。超過分は除外しました。');
      }

      setSendingBusy(true);
      try {
        if (images.length) {
          await sendDroppedImages(selectedArtist.artist_id, images);
          toastSuccess && toastSuccess('画像を送信しました。');
        }

        for (const f of others) {
          await sendDroppedFile(selectedArtist.artist_id, f);
        }
        if (others.length) {
          toastSuccess && toastSuccess(`${others.length}件のファイルを送信しました。`);
        }

        await handleComposerSent();
      } catch (e) {
        toastError && toastError(e?.message || 'アップロードに失敗しました。');
      } finally {
        setSendingBusy(false);
      }
    },
    [handleComposerSent, isReadOnly, selectedArtist, sendDroppedFile, sendDroppedImages, toastError, toastSuccess],
  );

  const { isDragActive, overlayRect } = useChatDragDrop({
    areaRef: chatContainerOuterRef,        // ★ 外枠を対象にしてデザインを統一
    highlightRef: chatContainerOuterRef,   // ★ ハイライトも同じ外枠
    enabled: !!selectedArtist,
    onDrop: handleDropFiles,
    onInvalid: (m) => toastError && toastError(m || 'ドラッグ＆ドロップは現在利用できません。'),
    maxBytes: 100 * 1024 * 1024,      // 100MB
  });
  useEffect(() => {
    const el = chatContainerOuterRef.current;
    if (!el) return;

    const handleTouchStart = (e) => {
      if (!isMobile || mobileView !== 'chat') {
        swipeStateRef.current = null;
        return;
      }
      const t = e.touches[0];
      swipeStateRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        locking: null,
        triggered: false,
      };
    };

    const handleTouchMove = (e) => {
      const data = swipeStateRef.current;
      if (!data || data.triggered || !isMobile || mobileView !== 'chat') return;
      const t = e.touches[0];
      const dx = t.clientX - data.startX;
      const dy = t.clientY - data.startY;

      if (!data.locking) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          data.locking = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        } else {
          return;
        }
      }
      if (data.locking !== 'x') return;

      if (dx <= -80 && Math.abs(dy) < 60) {
        data.triggered = true;
        goBackMobile();
      }
    };

    const clearSwipeState = () => {
      swipeStateRef.current = null;
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', clearSwipeState);
    el.addEventListener('touchcancel', clearSwipeState);
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', clearSwipeState);
      el.removeEventListener('touchcancel', clearSwipeState);
    };
  }, [isMobile, mobileView, goBackMobile]);

  // ルート要素のクラス（モバイル状態に応じて付与）
  const rootClasses = [
    'admin-message-container',
    'app-main',
    'app-main--force',
    isMobile ? 'is-mobile' : '',
    isMobile && mobileView === 'chat' ? 'is-mobile-chat' : '',
    isMobile && mobileView === 'list' ? 'is-mobile-list' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const closeChatSettingsModal = () => {
    setShowSettingsModal(false);
    setSettingsArtist(null);
    settingsArtistIdRef.current = null;
    settingsArtistRef.current = null;
    settingsOpenAllowsReadRef.current = false;
  };

  return {
    assigneeQuickToggles, displayedArtists, isPinnedArtist, handleTogglePinnedFromTargetMenu, handleArtistClick, openAiInbox,
    handleOpenAiTask, handleGoArtistDetail, handleOpenSettingsFromTargetMenu, handleSettingsButtonClick, clearSidebarLongPress, clearSidebarContextMenuBlock,
    handleSidebarTouchStart, handleReadNotifyToggle, handleArtistMuteToggle, imageMessages, imagePreviewUrls, imageFullUrls,
    imageModalItems, imageIndexByMessageId, displayMessages, handleImageClick, closeModal, showPrevImage,
    showNextImage, announcementSummaries, handleDownload, searchSummaryLabel, clearToolbarSearch, searchToolbarChips,
    isTargetSearchMode, showDelayedInitialLoading, shouldShowChatGuidance, openTargetSearchModal, applyTargetSearchModal, clearTargetSearchModal,
    handleBannerClick, handleNicknamePermissionChange, handleComposerSent, handleUploading, isDragActive, overlayRect,
    rootClasses, closeChatSettingsModal,
  };
}
